/* eslint-disable ember/no-runloop */
import PaperChips from 'ember-paper/components/paper-chips/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { next } from '@ember/runloop';
import { tagName } from '@ember-decorators/component';

@tagName('')
class SingleChip extends PaperChips {
  chipsElement = null;

  @tracked
  activeChip;

  @tracked
  searchText;

  focusInput() {
    // Selecting a chip can tear this component down, so the element may be
    // gone (or detached) by the time the runloop gets here.
    if (this.isDestroying || this.isDestroyed) {
      return;
    }

    this.chipsElement?.querySelector('.md-chip-input-container input')?.focus();
  }

  keyboardNavigation(ev, input) {
    if (
      this.activeChip >= 0 &&
      ['Backspace', 'Delete', 'Del'].includes(ev.key)
    ) {
      this.removeItem(this.content[this.activeChip]);
      this.activeChip = -1;
      // Focus on the next runloop, so that the autocomplete gets focused.
      next(this, 'focusInput');
    } else {
      // For all other keyboard events, use the paper-chips implementation.
      super.keyboardNavigation(ev, input);
    }
  }

  @action
  handleNoOptionsKeydown(ev) {
    if (
      [
        'Backspace',
        'Delete',
        'Del',
        'ArrowLeft',
        'Left',
        'ArrowRight',
        'Right',
      ].includes(ev.key)
    ) {
      this.keyboardNavigation(ev, ev.target);
    } else if (
      ev.key.length === 1 &&
      !ev.ctrlKey &&
      !ev.altKey &&
      !ev.metaKey
    ) {
      // Reject printable key presses
      ev.preventDefault();
      ev.stopPropagation();
      return false;
    }
  }

  @action
  handleRender(element) {
    // Track the chips element, since this component is tagless.
    this.chipsElement = element;
  }

  @action
  handleAutocompleteChange(item, select) {
    // This reimplements the paper-chips action rather than calling super,
    // because `addItem` often destroys this component (consumers swap the chip
    // out once a selection is made) and paper-chips then sets `searchText` on
    // the destroyed component, which asserts from Ember 4 onwards.
    if (item && select.isOpen) {
      if (this.addItem) {
        this.addItem(item);
      }

      select.actions.search('');

      if (!this.isDestroying && !this.isDestroyed) {
        this.searchText = '';
      }
    }

    // Focus the input after the autocomplete is replaced with a plain input.
    next(this, 'focusInput');
  }

  @action
  handleRemoveItem(item) {
    // Reimplemented for the same reason as `handleAutocompleteChange`:
    // `removeItem` can destroy this component, and paper-chips then sets
    // `activeChip` on the destroyed component.
    if (this.removeItem) {
      this.removeItem(item);
    }

    if (!this.isDestroying && !this.isDestroyed) {
      const current = this.activeChip;

      if (current === -1 || current >= this.content.length) {
        this.activeChip = -1;
      }
    }

    // Focus the autocomplete once it's replaced the plain input.
    next(this, 'focusInput');
  }
}

export default SingleChip;
